import { useState } from 'react';
import { Num, Plural, T, Var, useGT } from 'gt-react';
import { Orb, orbVariantForTool } from './Orb';
import styles from './ThinkingReasoning.module.css';

type TimelineEntry = { label: string; count: number };

function normalizeActionLabel(label: string): string {
  // Tool labels are “user facing” but still often include prefixes like
  // “Working on ...”. Normalize so the narrative reads cleanly.
  const withoutWorkingOn = label.replace(/^Working on\s+/i, '');
  const withoutEllipsis = withoutWorkingOn
    .replace(/…\s*$/u, '')
    .replace(/\.\.\.\s*$/u, '')
    .trim();
  return withoutEllipsis.replaceAll('_', ' ');
}

const TODO_LIST_GROUP = 'todo-list';

function narrativeGroupKey(normalized: string): string {
  if (/^set todo list$/i.test(normalized)) return TODO_LIST_GROUP;
  if (/^update todo items$/i.test(normalized)) return TODO_LIST_GROUP;
  if (/^clear todo list$/i.test(normalized)) return TODO_LIST_GROUP;
  return normalized;
}

function displayLabelForGroup(groupKey: string, normalized: string): string {
  if (groupKey === TODO_LIST_GROUP) return 'updated the task list';
  return normalized;
}

function timelineWithCounts(sentences: readonly string[]): TimelineEntry[] {
  const totals = new Map<string, number>();
  const labels = new Map<string, string>();
  for (const sentence of sentences) {
    const normalized = normalizeActionLabel(sentence);
    const groupKey = narrativeGroupKey(normalized);
    totals.set(groupKey, (totals.get(groupKey) ?? 0) + 1);
    labels.set(groupKey, displayLabelForGroup(groupKey, normalized));
  }

  const seen = new Set<string>();
  const entries: TimelineEntry[] = [];
  for (const sentence of sentences) {
    const normalized = normalizeActionLabel(sentence);
    const groupKey = narrativeGroupKey(normalized);
    if (seen.has(groupKey)) continue;
    seen.add(groupKey);
    entries.push({
      label: labels.get(groupKey) ?? normalized,
      count: totals.get(groupKey) ?? 1,
    });
  }
  return entries;
}

function capitalize(text: string): string {
  if (text.length === 0) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function lowercaseClause(text: string): string {
  if (text.length === 0) return text;
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function narrativeFromSentences(sentences: readonly string[]): string {
  const entries = timelineWithCounts(sentences);
  const clauses = entries
    .map((e) => lowercaseClause(e.label))
    .filter((c) => c.length > 0);

  if (clauses.length === 0) return '';
  const first = capitalize(clauses[0] ?? '');
  if (clauses.length === 1) return `${first}.`;
  if (clauses.length === 2) {
    const second = clauses[1];
    if (!second) return `${first}.`;
    return `${first} and ${second}.`;
  }
  const rest = clauses.slice(1);
  const last = rest.at(-1);
  if (!last) return `${first}.`;
  return `${first}, ${rest.slice(0, -1).join(', ')}, and ${last}.`;
}

function CountSuffix({ count }: { count: number }) {
  if (count <= 1) return null;
  return <span className={styles.trCountSuffix}>{` ×${count}`}</span>;
}

export function ThinkingReasoning({
  sentences,
  thinking,
  elapsedSeconds,
  liveToolName,
}: {
  sentences: string[];
  thinking: boolean;
  elapsedSeconds: number;
  /** Tool name for determining the live orb variant. */
  liveToolName?: string;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);

  const stepCount = sentences.length;
  if (stepCount === 0) {
    if (!thinking) return null;
    return (
      <div className={styles.tr}>
        <span className={`${styles.trLabel} ${styles.trShimmer}`}>
          <T context="label shown while the chat agent is calling tools">
            Thinking…
          </T>
        </span>
      </div>
    );
  }

  if (thinking) {
    const current = sentences.at(-1);
    if (!current) return null;
    return (
      <div className={styles.tr}>
        <div className={styles.trLive}>
          <Orb variant={orbVariantForTool(liveToolName ?? '')} size={18} />
          <span className={styles.trLiveLabel}>{current}</span>
        </div>
      </div>
    );
  }

  const seconds = Math.max(1, elapsedSeconds);
  const narrative = narrativeFromSentences(sentences);
  const entries = timelineWithCounts(sentences);

  return (
    <div className={styles.tr}>
      <button
        type="button"
        className={`${styles.trHeader} ${styles.isClickable}`}
        aria-expanded={open}
        aria-label={gt('Toggle thought', {
          context: 'aria-label to expand or collapse agent reasoning',
        })}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.trLabel}>
          <T context="collapsed reasoning header: Thought for Ns">
            Thought for <Var>{seconds}</Var>s
          </T>
          <span className={styles.trStepCount}>
            {' '}
            <T context="collapsed reasoning header: number of tool steps the agent took, e.g. (4 steps)">
              (<Num>{stepCount}</Num>{' '}
              <Plural n={stepCount} one="step" other="steps" />)
            </T>
          </span>
        </span>
        <svg
          className={styles.trChevron}
          viewBox="0 0 24 24"
          width="12"
          height="12"
          aria-hidden="true"
        >
          <path
            d="m4.5 15.75 7.5-7.5 7.5 7.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {narrative && !open ? (
        <p className={styles.trNarrative}>{narrative}</p>
      ) : null}

      {open ? (
        <ul className={styles.trDetailList}>
          {entries.map((entry) => (
            <li key={entry.label} className={styles.trDetailItem}>
              <span aria-hidden="true" className={styles.trDetailBullet}>
                ·
              </span>
              <span className={styles.trDetailText}>
                {entry.label}
                <CountSuffix count={entry.count} />
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
